import { createFileRoute } from '@tanstack/react-router';
import { useState, useEffect, ReactNode } from 'react';
import { useTenant } from '../lib/tenant-context';
import { tenantFetch } from '../lib/api-client';
import { RequireFeature } from '../components/RouteGuard';
import { Leaf as LeafIcon } from "@solar-icons/react";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, LineChart, Line, Legend, AreaChart, Area
} from 'recharts';
import { LayoutDashboard, Users, Coffee, MapPin, Recycle, RefreshCw, Leaf, Globe } from 'lucide-react';

export const Route = createFileRoute('/nespresso-analytics')({
  component: NespressoAnalyticsPage,
});

function NespressoAnalyticsPage() {
  return (
    <RequireFeature feature="nespresso_analytics" icon={<LeafIcon size={64} weight="LineDuotone" />}>
      <NespressoAnalytics />
    </RequireFeature>
  );
}

// Nespresso brand colors
const NESPRESSO_COLORS = {
  brown: '#6B4423',
  gold: '#C5A572',
  cream: '#F9F6F1',
  dark: '#2C1810',
  green: '#2D7D46',
};

const PIE_COLORS = ['#6B4423', '#C5A572', '#2D7D46', '#8B6914', '#4A4A4A'];

interface OverviewData {
  summary: {
    totalSessions: number;
    totalPods: number;
    totalCO2Saved: number;
    totalAluminum: number;
    uniqueCustomers: number;
  };
  trend: Array<{ date: string; pods: number; sessions: number }>;
}

interface CustomerData {
  tierDistribution: Array<{ tier: string; count: number; total_pods: number }>;
  topRecyclers: Array<{ id: string; name: string; tier: string; total_pods_recycled: number; co2_saved: number }>;
  returnFrequency: Record<string, number>;
  totalCustomers: number;
}

interface CapsuleData {
  systemDistribution: { vertuo: number; original: number; total: number };
  podTypeBreakdown: Array<{ name: string; count: number }>;
  vertuoPercentage: number;
}

interface ChannelData {
  channelBreakdown: Array<{ channel: string; sessions: number; pods: number; customers: number; avgPodsPerSession: number }>;
  topLocations: Array<{ id: string; name: string; city: string; sessions: number; pods: number }>;
}

interface MaterialData {
  materialRecovery: {
    totalPods: number;
    aluminumRecovered: number;
    coffeeGroundsComposted: number;
    co2Avoided: number;
  };
  qualityMetrics: {
    cleanPodsPercentage: number;
    contaminationRate: number;
    passedQC: number;
    failedQC: number;
  };
  contaminationTrend: Array<{ date: string; rate: number }>;
}

function NespressoAnalytics() {
  const { tenantId } = useTenant();
  const [activeTab, setActiveTab] = useState<'overview' | 'customers' | 'capsules' | 'channels' | 'materials'>('overview');
  const [loading, setLoading] = useState(true);
  const [overview, setOverview] = useState<OverviewData | null>(null);
  const [customers, setCustomers] = useState<CustomerData | null>(null);
  const [capsules, setCapsules] = useState<CapsuleData | null>(null);
  const [channels, setChannels] = useState<ChannelData | null>(null);
  const [materials, setMaterials] = useState<MaterialData | null>(null);

  useEffect(() => {
    loadData();
  }, [tenantId, activeTab]);

  const loadData = async () => {
    setLoading(true);
    try {
      const res = await tenantFetch(`/api/v1/nespresso/analytics?type=${activeTab}&days=30`);
      const contentType = res.headers.get('content-type');
      if (res.ok && contentType && contentType.includes('application/json')) {
        const data = await res.json();
        if (data.success) {
          switch (activeTab) {
            case 'overview':
              setOverview(data.data);
              return;
            case 'customers':
              setCustomers(data.data);
              return;
            case 'capsules':
              setCapsules(data.data);
              return;
            case 'channels':
              setChannels(data.data);
              return;
            case 'materials':
              setMaterials(data.data);
              return;
          }
        }
      }
      // Fallback to demo data if API fails or returns non-JSON
      loadDemoData();
    } catch (err) {
      console.error('Failed to load analytics:', err);
      loadDemoData();
    } finally {
      setLoading(false);
    }
  };

  const loadDemoData = () => {
    switch (activeTab) {
      case 'overview':
        setOverview(getDemoOverviewData());
        break;
      case 'customers':
        setCustomers(getDemoCustomerData());
        break;
      case 'capsules':
        setCapsules(getDemoCapsuleData());
        break;
      case 'channels':
        setChannels(getDemoChannelData());
        break;
      case 'materials':
        setMaterials(getDemoMaterialData());
        break;
    }
  };

  const tabs: { id: 'overview' | 'customers' | 'capsules' | 'channels' | 'materials'; label: string; icon: ReactNode }[] = [
    { id: 'overview', label: 'Overview', icon: <LayoutDashboard className="w-4 h-4" /> },
    { id: 'customers', label: 'Customers', icon: <Users className="w-4 h-4" /> },
    { id: 'capsules', label: 'Capsule Mix', icon: <Coffee className="w-4 h-4" /> },
    { id: 'channels', label: 'Channels', icon: <MapPin className="w-4 h-4" /> },
    { id: 'materials', label: 'Materials', icon: <Recycle className="w-4 h-4" /> },
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-light" style={{ color: NESPRESSO_COLORS.dark }}>
            Nespresso Recycling Analytics
          </h1>
          <p className="text-sm mt-1 text-gray-500">
            Pod recycling insights and performance metrics
          </p>
        </div>
        <div className="flex items-center gap-2 px-4 py-2 " style={{ backgroundColor: NESPRESSO_COLORS.cream }}>
          <Recycle className="w-5 h-5" style={{ color: NESPRESSO_COLORS.brown }} />
          <span className="font-light" style={{ color: NESPRESSO_COLORS.brown }}>
            Last 30 Days
          </span>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 border-b border-gray-200 pb-2">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`px-4 py-2  text-sm font-light transition-colors flex items-center gap-2 ${
              activeTab === tab.id
                ? 'text-white'
                : 'text-gray-600 hover:bg-gray-100'
            }`}
            style={activeTab === tab.id ? { backgroundColor: NESPRESSO_COLORS.brown } : {}}
          >
            <span>{tab.icon}</span>
            {tab.label}
          </button>
        ))}
      </div>

      {/* Content */}
      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-2 border-gray-300" 
               style={{ borderTopColor: NESPRESSO_COLORS.brown }} />
        </div>
      ) : (
        <>
          {activeTab === 'overview' && overview && <OverviewTab data={overview} />}
          {activeTab === 'customers' && customers && <CustomersTab data={customers} />}
          {activeTab === 'capsules' && capsules && <CapsulesTab data={capsules} />}
          {activeTab === 'channels' && channels && <ChannelsTab data={channels} />}
          {activeTab === 'materials' && materials && <MaterialsTab data={materials} />}
        </>
      )}
    </div>
  );
}

function OverviewTab({ data }: { data: OverviewData }) {
  const stats: { label: string; value: string; icon: ReactNode }[] = [
    { label: 'Total Pods Recycled', value: data.summary.totalPods.toLocaleString(), icon: <Coffee className="w-6 h-6" /> },
    { label: 'Recycling Sessions', value: data.summary.totalSessions.toLocaleString(), icon: <RefreshCw className="w-6 h-6" /> },
    { label: 'Unique Customers', value: data.summary.uniqueCustomers.toLocaleString(), icon: <Users className="w-6 h-6" /> },
    { label: 'CO₂ Saved (kg)', value: data.summary.totalCO2Saved.toFixed(1), icon: <Leaf className="w-6 h-6" /> },
    { label: 'Aluminum (kg)', value: data.summary.totalAluminum.toFixed(2), icon: <Recycle className="w-6 h-6" /> },
  ];

  return (
    <div className="space-y-6">
      {/* Stats Grid */}
      <div className="grid grid-cols-5 gap-4">
        {stats.map((stat) => (
          <div key={stat.label} className="p-4  shadow-sm" style={{ backgroundColor: NESPRESSO_COLORS.cream }}>
            <div className="mb-2" style={{ color: NESPRESSO_COLORS.brown }}>{stat.icon}</div>
            <div className="text-2xl font-light" style={{ color: NESPRESSO_COLORS.brown }}>{stat.value}</div>
            <div className="text-xs text-gray-500 mt-1">{stat.label}</div>
          </div>
        ))}
      </div>

      {/* Trend Chart */}
      <div className="p-6 bg-white  shadow-sm">
        <h3 className="text-lg font-light mb-4" style={{ color: NESPRESSO_COLORS.dark }}>
          Daily Recycling Trend
        </h3>
        <ResponsiveContainer width="100%" height={300} minWidth={100} minHeight={100}>
          <AreaChart data={data.trend}>
            <CartesianGrid strokeDasharray="3 3" stroke="#E5E5E5" />
            <XAxis dataKey="date" tick={{ fontSize: 12 }} />
            <YAxis tick={{ fontSize: 12 }} />
            <Tooltip />
            <Area 
              type="monotone" 
              dataKey="pods" 
              stroke={NESPRESSO_COLORS.brown} 
              fill={NESPRESSO_COLORS.gold}
              fillOpacity={0.3}
              name="Pods Recycled"
            />
          </AreaChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

function CustomersTab({ data }: { data: CustomerData }) {
  const tierData = data.tierDistribution.map(t => ({
    name: t.tier.charAt(0).toUpperCase() + t.tier.slice(1),
    value: t.count,
    pods: t.total_pods,
  }));

  const frequencyData = Object.entries(data.returnFrequency).map(([key, value]) => ({
    name: key === '1' ? 'One-time' : key === '10+' ? 'Frequent (10+)' : `${key} visits`,
    value,
  }));

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 gap-6">
        {/* Tier Distribution */}
        <div className="p-6 bg-white  shadow-sm">
          <h3 className="text-lg font-light mb-4" style={{ color: NESPRESSO_COLORS.dark }}>
            Loyalty Tier Distribution
          </h3>
          <ResponsiveContainer width="100%" height={250} minWidth={100} minHeight={100}>
            <PieChart>
              <Pie
                data={tierData}
                cx="50%"
                cy="50%"
                innerRadius={60}
                outerRadius={100}
                dataKey="value"
                label={({ name, percent }) => `${name} (${((percent || 0) * 100).toFixed(0)}%)`}
              >
                {tierData.map((_, index) => (
                  <Cell key={`cell-${index}`} fill={PIE_COLORS[index % PIE_COLORS.length]} />
                ))}
              </Pie>
              <Tooltip />
            </PieChart>
          </ResponsiveContainer>
          <div className="text-center mt-2 text-sm text-gray-500">
            {data.totalCustomers} Total Customers
          </div>
        </div>

        {/* Return Frequency */}
        <div className="p-6 bg-white  shadow-sm">
          <h3 className="text-lg font-light mb-4" style={{ color: NESPRESSO_COLORS.dark }}>
            Return Frequency
          </h3>
          <ResponsiveContainer width="100%" height={250} minWidth={100} minHeight={100}>
            <BarChart data={frequencyData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E5E5E5" />
              <XAxis dataKey="name" tick={{ fontSize: 11 }} />
              <YAxis tick={{ fontSize: 12 }} />
              <Tooltip />
              <Bar dataKey="value" fill={NESPRESSO_COLORS.brown} radius={[4, 4, 0, 0]} name="Customers" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Top Recyclers */}
      <div className="p-6 bg-white  shadow-sm">
        <h3 className="text-lg font-light mb-4" style={{ color: NESPRESSO_COLORS.dark }}>
          Top Recyclers
        </h3>
        <table className="w-full">
          <thead>
            <tr className="text-left text-sm text-gray-500 border-b">
              <th className="pb-2">Customer</th>
              <th className="pb-2">Tier</th>
              <th className="pb-2 text-right">Pods Recycled</th>
              <th className="pb-2 text-right">CO₂ Saved (kg)</th>
            </tr>
          </thead>
          <tbody>
            {data.topRecyclers.map((customer, i) => (
              <tr key={customer.id} className="border-b border-gray-100">
                <td className="py-3 flex items-center gap-2">
                  <span className="w-6 h-6 rounded-full flex items-center justify-center text-white text-xs"
                        style={{ backgroundColor: NESPRESSO_COLORS.brown }}>
                    {i + 1}
                  </span>
                  {customer.name}
                </td>
                <td className="py-3">
                  <span className={`px-2 py-1 rounded-full text-xs font-light ${
                    customer.tier === 'platinum' ? 'bg-gray-800 text-white' :
                    customer.tier === 'gold' ? 'bg-yellow-100 text-yellow-800' :
                    customer.tier === 'silver' ? 'bg-gray-200 text-gray-700' :
                    'bg-amber-100 text-amber-800'
                  }`}>
                    {customer.tier.charAt(0).toUpperCase() + customer.tier.slice(1)}
                  </span>
                </td>
                <td className="py-3 text-right font-light">{customer.total_pods_recycled.toLocaleString()}</td>
                <td className="py-3 text-right text-gray-500">{customer.co2_saved.toFixed(1)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function CapsulesTab({ data }: { data: CapsuleData }) {
  const systemData = [
    { name: 'Vertuo', value: data.systemDistribution.vertuo, color: '#005F73' },
    { name: 'Original', value: data.systemDistribution.original, color: '#0A9396' },
  ];

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 gap-6">
        {/* System Distribution */}
        <div className="p-6 bg-white  shadow-sm">
          <h3 className="text-lg font-light mb-4" style={{ color: NESPRESSO_COLORS.dark }}>
            Capsule System Distribution
          </h3>
          <ResponsiveContainer width="100%" height={250} minWidth={100} minHeight={100}>
            <PieChart>
              <Pie
                data={systemData}
                cx="50%"
                cy="50%"
                innerRadius={60}
                outerRadius={100}
                dataKey="value"
                label={({ name, percent }) => `${name} (${((percent || 0) * 100).toFixed(0)}%)`}
              >
                {systemData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.color} />
                ))}
              </Pie>
              <Tooltip />
            </PieChart>
          </ResponsiveContainer>
          <div className="text-center mt-4">
            <span className="text-2xl font-light" style={{ color: NESPRESSO_COLORS.brown }}>
              {data.systemDistribution.total.toLocaleString()}
            </span>
            <span className="text-sm text-gray-500 ml-2">Total Capsules</span>
          </div>
        </div>

        {/* Pod Type Breakdown */}
        <div className="p-6 bg-white  shadow-sm">
          <h3 className="text-lg font-light mb-4" style={{ color: NESPRESSO_COLORS.dark }}>
            Most Recycled Varieties
          </h3>
          <ResponsiveContainer width="100%" height={250} minWidth={100} minHeight={100}>
            <BarChart data={data.podTypeBreakdown} layout="vertical">
              <CartesianGrid strokeDasharray="3 3" stroke="#E5E5E5" />
              <XAxis type="number" tick={{ fontSize: 12 }} />
              <YAxis type="category" dataKey="name" tick={{ fontSize: 12 }} width={80} />
              <Tooltip />
              <Bar dataKey="count" fill={NESPRESSO_COLORS.gold} radius={[0, 4, 4, 0]} name="Capsules" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
}

function ChannelsTab({ data }: { data: ChannelData }) {
  const channelData = data.channelBreakdown.map(c => ({
    ...c,
    channel: c.channel.charAt(0).toUpperCase() + c.channel.slice(1),
  }));

  return (
    <div className="space-y-6">
      {/* Channel Performance */}
      <div className="p-6 bg-white  shadow-sm">
        <h3 className="text-lg font-light mb-4" style={{ color: NESPRESSO_COLORS.dark }}>
          Return Channel Performance
        </h3>
        <ResponsiveContainer width="100%" height={300} minWidth={100} minHeight={100}>
          <BarChart data={channelData}>
            <CartesianGrid strokeDasharray="3 3" stroke="#E5E5E5" />
            <XAxis dataKey="channel" tick={{ fontSize: 12 }} />
            <YAxis tick={{ fontSize: 12 }} />
            <Tooltip />
            <Legend />
            <Bar dataKey="pods" fill={NESPRESSO_COLORS.brown} name="Pods" radius={[4, 4, 0, 0]} />
            <Bar dataKey="sessions" fill={NESPRESSO_COLORS.gold} name="Sessions" radius={[4, 4, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </div>

      {/* Top Locations */}
      <div className="p-6 bg-white  shadow-sm">
        <h3 className="text-lg font-light mb-4" style={{ color: NESPRESSO_COLORS.dark }}>
          Top Kiosk Locations
        </h3>
        <div className="grid grid-cols-2 gap-4">
          {data.topLocations.map((location, i) => (
            <div 
              key={location.id} 
              className="p-4  border border-gray-100 flex items-center gap-4"
              style={{ backgroundColor: i === 0 ? NESPRESSO_COLORS.cream : 'white' }}
            >
              <div className="w-10 h-10 rounded-full flex items-center justify-center text-white font-light"
                   style={{ backgroundColor: NESPRESSO_COLORS.brown }}>
                {i + 1}
              </div>
              <div className="flex-1">
                <div className="font-light" style={{ color: NESPRESSO_COLORS.dark }}>{location.name}</div>
                <div className="text-sm text-gray-500">{location.city}</div>
              </div>
              <div className="text-right">
                <div className="font-light" style={{ color: NESPRESSO_COLORS.brown }}>
                  {location.pods.toLocaleString()} pods
                </div>
                <div className="text-xs text-gray-500">{location.sessions} sessions</div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function MaterialsTab({ data }: { data: MaterialData }) {
  return (
    <div className="space-y-6">
      {/* Material Recovery Stats */}
      <div className="grid grid-cols-4 gap-4">
        <div className="p-5 bg-white  shadow-sm text-center">
          <div className="flex justify-center mb-2" style={{ color: NESPRESSO_COLORS.brown }}>
            <Coffee className="w-8 h-8" />
          </div>
          <div className="text-2xl font-light" style={{ color: NESPRESSO_COLORS.brown }}>
            {data.materialRecovery.totalPods.toLocaleString()}
          </div>
          <div className="text-sm text-gray-500">Pods Processed</div>
        </div>
        <div className="p-5 bg-white  shadow-sm text-center">
          <div className="flex justify-center mb-2" style={{ color: NESPRESSO_COLORS.brown }}>
            <Recycle className="w-8 h-8" />
          </div>
          <div className="text-2xl font-light" style={{ color: NESPRESSO_COLORS.brown }}>
            {data.materialRecovery.aluminumRecovered.toFixed(2)} kg
          </div>
          <div className="text-sm text-gray-500">Aluminum Recovered</div>
        </div>
        <div className="p-5 bg-white  shadow-sm text-center">
          <div className="flex justify-center mb-2" style={{ color: NESPRESSO_COLORS.green }}>
            <Leaf className="w-8 h-8" />
          </div>
          <div className="text-2xl font-light" style={{ color: NESPRESSO_COLORS.green }}>
            {data.materialRecovery.coffeeGroundsComposted.toFixed(2)} kg
          </div>
          <div className="text-sm text-gray-500">Coffee Composted</div>
        </div>
        <div className="p-5 bg-white  shadow-sm text-center">
          <div className="flex justify-center mb-2" style={{ color: NESPRESSO_COLORS.green }}>
            <Globe className="w-8 h-8" />
          </div>
          <div className="text-2xl font-light" style={{ color: NESPRESSO_COLORS.green }}>
            {data.materialRecovery.co2Avoided.toFixed(1)} kg
          </div>
          <div className="text-sm text-gray-500">CO₂ Avoided</div>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-6">
        {/* Quality Metrics */}
        <div className="p-6 bg-white  shadow-sm">
          <h3 className="text-lg font-light mb-4" style={{ color: NESPRESSO_COLORS.dark }}>
            Quality Control
          </h3>
          <div className="flex items-center justify-center">
            <div className="relative w-48 h-48">
              <svg className="transform -rotate-90" width="192" height="192" viewBox="0 0 192 192">
                <circle cx="96" cy="96" r="80" fill="none" stroke="#E5E5E5" strokeWidth="16" />
                <circle 
                  cx="96" cy="96" r="80" 
                  fill="none" 
                  stroke={NESPRESSO_COLORS.green}
                  strokeWidth="16"
                  strokeDasharray={`${data.qualityMetrics.cleanPodsPercentage * 5.02} 502`}
                  strokeLinecap="round"
                />
              </svg>
              <div className="absolute inset-0 flex flex-col items-center justify-center">
                <span className="text-3xl font-light" style={{ color: NESPRESSO_COLORS.brown }}>
                  {data.qualityMetrics.cleanPodsPercentage}%
                </span>
                <span className="text-sm text-gray-500">Pass Rate</span>
              </div>
            </div>
          </div>
          <div className="mt-4 grid grid-cols-2 gap-4 text-center">
            <div>
              <div className="text-lg font-light" style={{ color: NESPRESSO_COLORS.green }}>
                {data.qualityMetrics.passedQC.toLocaleString()}
              </div>
              <div className="text-xs text-gray-500">Passed QC</div>
            </div>
            <div>
              <div className="text-lg font-light text-red-500">
                {data.qualityMetrics.failedQC.toLocaleString()}
              </div>
              <div className="text-xs text-gray-500">Failed QC</div>
            </div>
          </div>
        </div>

        {/* Contamination Trend */}
        <div className="p-6 bg-white  shadow-sm">
          <h3 className="text-lg font-light mb-4" style={{ color: NESPRESSO_COLORS.dark }}>
            Contamination Rate Trend
          </h3>
          <ResponsiveContainer width="100%" height={200} minWidth={100} minHeight={100}>
            <LineChart data={data.contaminationTrend}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E5E5E5" />
              <XAxis dataKey="date" tick={{ fontSize: 10 }} />
              <YAxis tick={{ fontSize: 12 }} domain={[0, 20]} unit="%" />
              <Tooltip formatter={(value) => [`${value}%`, 'Contamination Rate']} />
              <Line 
                type="monotone" 
                dataKey="rate" 
                stroke="#DC2626" 
                strokeWidth={2}
                dot={{ fill: '#DC2626', r: 3 }}
              />
            </LineChart>
          </ResponsiveContainer>
          <div className="text-center mt-2 text-sm text-gray-500">
            Target: &lt;5% contamination
          </div>
        </div>
      </div>
    </div>
  );
}

// Demo data functions
function getDemoOverviewData(): OverviewData {
  const trend: Array<{ date: string; pods: number; sessions: number }> = [];
  const now = new Date();
  for (let i = 29; i >= 0; i--) {
    const date = new Date(now);
    date.setDate(date.getDate() - i);
    const dayOfWeek = date.getDay();
    const basePods = dayOfWeek === 0 || dayOfWeek === 6 ? 180 : 120;
    const seed = date.getDate() + date.getMonth() * 31;
    const variation = (seed % 60) - 30;
    trend.push({
      date: date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
      pods: basePods + variation,
      sessions: Math.round((basePods + variation) / 15),
    });
  }

  return {
    summary: {
      totalSessions: 847,
      totalPods: 12543,
      totalCO2Saved: 62.7,
      totalAluminum: 12.54,
      uniqueCustomers: 412,
    },
    trend,
  };
}

function getDemoCustomerData(): CustomerData {
  return {
    tierDistribution: [
      { tier: 'platinum', count: 45, total_pods: 4500 },
      { tier: 'gold', count: 120, total_pods: 4800 },
      { tier: 'silver', count: 180, total_pods: 2700 },
      { tier: 'bronze', count: 67, total_pods: 543 },
    ],
    topRecyclers: [
      { id: 'c1', name: 'Maria Santos', tier: 'platinum', total_pods_recycled: 892, co2_saved: 4.46 },
      { id: 'c2', name: 'James Chen', tier: 'platinum', total_pods_recycled: 756, co2_saved: 3.78 },
      { id: 'c3', name: 'Sophie Martin', tier: 'gold', total_pods_recycled: 621, co2_saved: 3.11 },
      { id: 'c4', name: 'Lucas Schmidt', tier: 'gold', total_pods_recycled: 543, co2_saved: 2.72 },
      { id: 'c5', name: 'Emma Wilson', tier: 'gold', total_pods_recycled: 489, co2_saved: 2.45 },
    ],
    returnFrequency: {
      '1': 145,
      '2-3': 98,
      '4-6': 87,
      '7-9': 52,
      '10+': 30,
    },
    totalCustomers: 412,
  };
}

function getDemoCapsuleData(): CapsuleData {
  return {
    systemDistribution: {
      vertuo: 7234,
      original: 5309,
      total: 12543,
    },
    podTypeBreakdown: [
      { name: 'Ristretto', count: 2145 },
      { name: 'Espresso', count: 1987 },
      { name: 'Lungo', count: 1654 },
      { name: 'Double Espresso', count: 1432 },
      { name: 'Gran Lungo', count: 1287 },
      { name: 'Alto', count: 1098 },
      { name: 'Mug', count: 876 },
      { name: 'Carafe', count: 542 },
    ],
    vertuoPercentage: 57.7,
  };
}

function getDemoChannelData(): ChannelData {
  return {
    channelBreakdown: [
      { channel: 'boutique', sessions: 312, pods: 4680, customers: 187, avgPodsPerSession: 15 },
      { channel: 'kiosk', sessions: 287, pods: 4018, customers: 156, avgPodsPerSession: 14 },
      { channel: 'mail-in', sessions: 156, pods: 2496, customers: 89, avgPodsPerSession: 16 },
      { channel: 'partner', sessions: 92, pods: 1349, customers: 67, avgPodsPerSession: 14.7 },
    ],
    topLocations: [
      { id: 'loc1', name: 'Nespresso Boutique SoHo', city: 'New York', sessions: 89, pods: 1335 },
      { id: 'loc2', name: 'Nespresso Boutique Beverly Hills', city: 'Los Angeles', sessions: 76, pods: 1140 },
      { id: 'loc3', name: 'Nespresso Kiosk Westfield', city: 'San Francisco', sessions: 68, pods: 952 },
      { id: 'loc4', name: 'Nespresso Boutique Michigan Ave', city: 'Chicago', sessions: 54, pods: 810 },
    ],
  };
}

function getDemoMaterialData(): MaterialData {
  const contaminationTrend: Array<{ date: string; rate: number }> = [];
  const now = new Date();
  for (let i = 29; i >= 0; i--) {
    const date = new Date(now);
    date.setDate(date.getDate() - i);
    const seed = date.getDate() + date.getMonth() * 31;
    const baseRate = 3.5;
    const variation = (seed % 30) / 10 - 1.5;
    contaminationTrend.push({
      date: date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
      rate: Math.max(0.5, Math.min(8, baseRate + variation)),
    });
  }

  return {
    materialRecovery: {
      totalPods: 12543,
      aluminumRecovered: 12.54,
      coffeeGroundsComposted: 87.8,
      co2Avoided: 62.7,
    },
    qualityMetrics: {
      cleanPodsPercentage: 94.2,
      contaminationRate: 3.8,
      passedQC: 11816,
      failedQC: 727,
    },
    contaminationTrend,
  };
}

